import { jsx as _jsx } from "react/jsx-runtime";
import { OktaContext } from '@okta/okta-react';
import React, { useEffect, Suspense, useContext, useState } from 'react';
import { AccountAPI } from 'api/account';
import { UserAPI } from 'api/user';
import { PageLoading } from 'components/atoms/loading';
import { LoginPage } from 'components/organisms/login/login';
import { TokenErrorStatus } from 'components/organisms/token-error/constant';
import { useCustomAuth } from 'hooks/use-custom-auth';
import { useQueryParams } from 'hooks/use-query-params';
import { useAccountMetaQuery } from 'queries';
import { ActiveTab, TokenType } from 'services';
import { useDismissBootSkeleton } from 'utils/dismiss-boot-skeleton';
import { isTauriApp } from 'utils/is-tauri';
import { logout } from 'utils/logout';
import { isNonBrowserMagicLinkAgent } from 'utils/magic-link-agent';
import { redirectToOAuth } from 'utils/oauth-redirect';
import { getDecodedEmail, getTokenMeta, isLinkMagicDecoded, isLinkMagicToken, isResendableLinkMagic } from 'utils/token';
import { LoginWithToken } from '../login-with-token';
import { DesktopMagicLinkBounce } from 'components/molecules/desktop-magic-link-bounce';
const TokenError = /*#__PURE__*/ React.lazy(()=>import('components/organisms/token-error/token-error').then((d)=>({
            default: d.TokenError
        })));
// Effect + hard nav (not <Navigate>) — pop() must run once, and SPA nav races auth state.
const RestoreSavedTab = ()=>{
    useEffect(()=>{
        const saved = ActiveTab.pop();
        let target = '/';
        try {
            target = saved ? new URL(saved).href.replace(window.location.origin, '') || '/' : '/';
        } catch (unused) {
            target = '/';
        }
        window.location.replace(target);
    }, []);
    return /*#__PURE__*/ _jsx(PageLoading, {});
};
export const Login = ()=>{
    var _params_get, _params_get1, _params_get2, _params_get3;
    var _accountMeta_okta, _accountMeta_okta1;
    // /login is a public route — it doesn't mount inside RouterShell, so the
    // static #rl-boot-skeleton overlay from public/index.html stays visible
    // (left-rail squares + content title placeholders) on top of the login
    // page or the magic-link <TokenError /> until something dismisses it.
    // Dismiss it here, matching the pattern in goodbye.tsx / render-portal.tsx.
    useDismissBootSkeleton();
    const oktaContext = useContext(OktaContext);
    const { isAuthenticated: customIsAuthenticated } = useCustomAuth();
    const authState = oktaContext ? oktaContext.authState : {
        isAuthenticated: customIsAuthenticated
    };
    const { data: accountMeta } = useAccountMetaQuery();
    const params = useQueryParams();
    const token = (_params_get = params.get('token')) !== null && _params_get !== void 0 ? _params_get : '';
    const cognitoType = (_params_get1 = params.get('cognito')) !== null && _params_get1 !== void 0 ? _params_get1 : '';
    const extToken = (_params_get2 = params.get('rl-token')) !== null && _params_get2 !== void 0 ? _params_get2 : '';
    const agent = params.get('agent');
    const overrideAuth = (_params_get3 = params.get('overrideAuth')) !== null && _params_get3 !== void 0 ? _params_get3 : false;
    const { isExpired, decoded } = getTokenMeta(token);
    const resolvedToken = token || extToken || '';
    const [continueInBrowser, setContinueInBrowser] = useState(false);
    useEffect(()=>{
        var _accountMeta_okta, _accountMeta_okta1;
        // Skip when a magic-link token is present — LoginWithToken owns that redirect and
        // this effect would otherwise race it, losing the token en route to id.
        if (token || extToken) {
            return;
        }
        // Desktop shell must not bounce into browser OAuth.
        if (isTauriApp()) {
            return;
        }
        if ((accountMeta === null || accountMeta === void 0 ? void 0 : (_accountMeta_okta = accountMeta.okta) === null || _accountMeta_okta === void 0 ? void 0 : _accountMeta_okta.cookiesEnabled) && (accountMeta === null || accountMeta === void 0 ? void 0 : (_accountMeta_okta1 = accountMeta.okta) === null || _accountMeta_okta1 === void 0 ? void 0 : _accountMeta_okta1.clientId) && (authState === null || authState === void 0 ? void 0 : authState.isAuthenticated) === false) {
            // saveIfEmpty — don't clobber a deep link UnauthenticatedRedirector already captured.
            ActiveTab.saveIfEmpty();
            redirectToOAuth(accountMeta.okta.clientId);
        }
    }, [
        token,
        extToken,
        accountMeta === null || accountMeta === void 0 ? void 0 : (_accountMeta_okta = accountMeta.okta) === null || _accountMeta_okta === void 0 ? void 0 : _accountMeta_okta.clientId,
        accountMeta === null || accountMeta === void 0 ? void 0 : (_accountMeta_okta1 = accountMeta.okta) === null || _accountMeta_okta1 === void 0 ? void 0 : _accountMeta_okta1.cookiesEnabled,
        authState === null || authState === void 0 ? void 0 : authState.isAuthenticated
    ]);
    // Non-browser agents: bounce to the desktop app — do not redeem on web
    // unless the custom scheme does not take over.
    if (resolvedToken && isNonBrowserMagicLinkAgent(agent) && !continueInBrowser) {
        return /*#__PURE__*/ _jsx(DesktopMagicLinkBounce, {
            token: resolvedToken,
            agent: agent !== null && agent !== void 0 ? agent : '',
            onContinueInBrowser: ()=>setContinueInBrowser(true)
        });
    }
    if (decoded && isExpired) {
        const email = getDecodedEmail(decoded);
        // LINK_MAGIC → resendMagicLink (resendable types only; others get no resend); legacy → getMagicLink.
        const resendEmail = isLinkMagicToken(token) ? isResendableLinkMagic(decoded) ? ()=>AccountAPI.resendMagicLink(token, agent) : undefined : ()=>UserAPI.getMagicLink(email);
        const linkType = isLinkMagicDecoded(decoded) ? decoded.link_type : decoded.linkType;
        return /*#__PURE__*/ _jsx(Suspense, {
            fallback: /*#__PURE__*/ _jsx(PageLoading, {}),
            children: /*#__PURE__*/ _jsx(TokenError, {
                status: TokenErrorStatus.Expired,
                email: email,
                resendEmail: resendEmail,
                linkType: linkType
            })
        });
    }
    if (overrideAuth) {
        logout(`?token=${token}`);
        return /*#__PURE__*/ _jsx(PageLoading, {});
    }
    // LINK_MAGIC always goes through LoginWithToken, even with an existing rl_session —
    // the action must run as the token's user, not silently short-circuit to <Navigate />.
    const isLinkMagic = Boolean(token) && isLinkMagicToken(token);
    return !authState ? /*#__PURE__*/ _jsx(PageLoading, {}) : isLinkMagic ? /*#__PURE__*/ _jsx(LoginWithToken, {
        token: token,
        tokenType: TokenType.Rocketlane
    }) : token || extToken ? // Must take precedence over the authenticated branch below — otherwise the consume's
    // auth flip triggers a second ActiveTab.pop() (now empty), dropping the user on home.
    /*#__PURE__*/ _jsx(LoginWithToken, {
        token: token || extToken || '',
        tokenType: extToken ? TokenType.External : cognitoType ? TokenType.Cognito : TokenType.Rocketlane
    }) : authState.isAuthenticated ? /*#__PURE__*/ _jsx(RestoreSavedTab, {}) : /*#__PURE__*/ _jsx(LoginPage, {});
};
